import { useMemo } from 'react'

export type CalendarDay<T> = {
  date: Date
  day: number
  isCurrentMonth: boolean
  items: T[]
}

type UseCalendarProps<T> = {
  year: number
  month: number
  itemsByDate: Map<string, T[]>
}

export function useCalendar<T>({ year, month, itemsByDate }: UseCalendarProps<T>) {
  const today = new Date()

  const calendarDays = useMemo<CalendarDay<T>[]>(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    let firstDayOfWeek = new Date(year, month, 1).getDay()
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1

    const prevMonthDays = new Date(year, month, 0).getDate()

    const days: CalendarDay<T>[] = []

    let nextMonthDay = 1

    // Дни предыдущего месяца
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayNumber = prevMonthDays - i

      days.push({
        date: new Date(year, month - 1, dayNumber),
        day: dayNumber,
        isCurrentMonth: false,
        items: [],
      })
    }

    // Дни текущего месяца
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = new Date(year, month, i)

      const dateKey = [
        currentDay.getFullYear(),
        String(currentDay.getMonth() + 1).padStart(2, '0'),
        String(currentDay.getDate()).padStart(2, '0'),
      ].join('-')

      days.push({
        date: currentDay,
        day: i,
        isCurrentMonth: true,
        items: itemsByDate.get(dateKey) || [],
      })
    }

    while (days.length < 42) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        day: nextMonthDay,
        isCurrentMonth: false,
        items: [],
      })

      nextMonthDay++
    }

    return days
  }, [year, month, itemsByDate])

  const weeks = useMemo(() => {
    const result: CalendarDay<T>[][] = []

    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7))
    }

    return result
  }, [calendarDays])

  const isToday = (date: Date) => {
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  return {
    calendarDays,
    weeks,
    isToday,
  }
}
